import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Mail, Phone, MapPin, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { StatusPill, STATUS_LABELS } from "@/components/Status";

const SIZING_FIELDS = [
  { key: "shirt", label: "Shirt" },
  { key: "hat", label: "Hat" },
  { key: "outerwear", label: "Outerwear" },
  { key: "shoe", label: "Shoe" },
  { key: "belt", label: "Belt" },
];

/**
 * Staff-side combined Member + Crest Profile dossier.
 * Loads /api/admin/members/{user_id} which returns the member's contact info,
 * sizing, style/brand/category preferences, recent orders and wishlist.
 */
export default function AdminMemberDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/admin/members/${userId}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => {
        if (!cancelled) setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      });
    return () => { cancelled = true; };
  }, [userId]);

  const handleNumberSaved = (updatedMember) => {
    setData((d) => (d ? { ...d, member: updatedMember } : d));
  };

  if (error) {
    return (
      <div className="bg-cream min-h-screen">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-12">
          <BackLink />
          <div className="mt-6 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm">{error}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-cream min-h-screen">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-12">
          <BackLink />
          <p className="mt-10 text-[#7A7A7A]">Loading dossier…</p>
        </div>
      </div>
    );
  }

  const { member, orders, wishlist } = data;
  const homeClub = (member.clubs || []).find((c) => c.is_home) || (member.clubs || [])[0];
  const sizingPretty = SIZING_FIELDS
    .map((f) => ({ ...f, value: (member.sizing || {})[f.key] }))
    .filter((f) => f.value);

  return (
    <div className="bg-cream min-h-screen" data-testid="admin-member-detail">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-12">
        <BackLink />

        <div className="mt-6 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="overline">Member Dossier</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-2 leading-none">
              {member.name || "Member"}
            </h1>
            <p className="text-[#4A4A4A] mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="inline-flex items-center gap-1.5"><Mail size={14} /> {member.email}</span>
              {(member.mobile_number || member.phone) && (
                <span className="inline-flex items-center gap-1.5"><Phone size={14} /> {member.mobile_number || member.phone}</span>
              )}
              {homeClub && (
                <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {homeClub.name} · #{homeClub.member_number}</span>
              )}
            </p>
          </div>
          <div className="text-right text-xs text-[#7A7A7A]">
            {member.created_at && <div>Joined {new Date(member.created_at).toLocaleDateString()}</div>}
            {member.onboarding_completed_at && (
              <div className="mt-1 inline-block px-2 py-1 border border-[#1A422D] text-[#1A422D] bg-[#E9F0EB] uppercase tracking-wider">
                Crest Profile Complete
              </div>
            )}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: Crest Profile */}
          <div className="lg:col-span-1 space-y-6">
            <Section title="Sizing" subtitle="Private — never shared with vendors.">
              {sizingPretty.length === 0 ? (
                <Empty>Not set</Empty>
              ) : (
                <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                  {sizingPretty.map((f) => (
                    <div key={f.key}>
                      <dt className="label-elegant">{f.label}</dt>
                      <dd className="text-[#1A1A1A] mt-1">{f.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </Section>
            <Section title="Style Preferences">
              <Chips items={member.style_preferences} />
            </Section>
            <Section title="Favorite Brands">
              <Chips items={member.favorite_brands} accent="brass" />
            </Section>
            <Section title="Favorite Categories">
              <Chips items={member.favorite_categories} />
            </Section>
            {(member.logo_preferences || []).length > 0 && (
              <Section title="Logo & Monogram">
                <Chips items={member.logo_preferences} />
              </Section>
            )}
            {(member.clubs || []).length > 0 && (
              <Section title="Club Affiliations">
                <ul className="space-y-2">
                  {member.clubs.map((c, i) => (
                    <ClubAffiliationRow
                      key={`${c.name}-${i}`}
                      club={c}
                      userId={member.user_id}
                      onSaved={handleNumberSaved}
                    />
                  ))}
                </ul>
              </Section>
            )}
          </div>

          {/* Right column: Orders + Wishlist */}
          <div className="lg:col-span-2 space-y-6">
            <Section
              title="Recent Orders"
              subtitle={`${orders.length} active${orders.length === 50 ? "+" : ""} on file`}
              overline="Activity"
              data-testid="member-orders"
            >
              {orders.length === 0 ? (
                <Empty>No orders yet.</Empty>
              ) : (
                <ul className="divide-y divide-[#E5E3D8]">
                  {orders.map((o) => (
                    <li key={o.order_id} className="py-3 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <Link
                          to={`/orders/${o.order_id}`}
                          className="font-serif text-lg text-[#1A1A1A] hover:underline underline-offset-4"
                        >
                          {o.item_name}
                        </Link>
                        <div className="text-xs text-[#7A7A7A] mt-0.5">
                          {[o.category, `Qty ${o.quantity}`, o.created_at ? new Date(o.created_at).toLocaleDateString() : null]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </div>
                      <StatusPill status={o.status} />
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section
              title="Wishlist"
              subtitle={`${wishlist.length} item${wishlist.length === 1 ? "" : "s"} saved`}
              overline="Activity"
              data-testid="member-wishlist"
            >
              {wishlist.length === 0 ? (
                <Empty>Nothing saved yet.</Empty>
              ) : (
                <ul className="divide-y divide-[#E5E3D8]">
                  {wishlist.map((w) => (
                    <li key={w.wishlist_id} className="py-3 flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="font-serif text-lg text-[#1A1A1A]">{w.item_name}</div>
                        <div className="text-xs text-[#7A7A7A] mt-0.5">
                          {[w.brand, w.category, w.collection, w.where_seen]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </div>
                      {w.share_with_club && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-1 border border-[#B59A5A] text-[#8a6f33] bg-[#FBF6E8] whitespace-nowrap">
                          Shared
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>
        </div>

        <RemoveMemberPanel
          userId={member.user_id}
          email={member.email}
          name={member.name}
          onRemoved={() => navigate("/admin/members")}
        />
      </div>
    </div>
  );
}

function RemoveMemberPanel({ userId, email, name, onRemoved }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const expected = (email || "").trim().toLowerCase();
  const matches = typed.trim().toLowerCase() === expected && expected.length > 0;

  const reset = () => { setOpen(false); setTyped(""); setFeedback(null); };

  const submit = async () => {
    if (!matches) {
      setFeedback({ kind: "error", text: "Type the member's email exactly to confirm." });
      return;
    }
    setBusy(true);
    setFeedback(null);
    try {
      const { data: resp } = await api.delete(
        `/admin/members/${userId}`,
        { params: { confirm: expected } },
      );
      setFeedback({
        kind: "ok",
        text: resp.user_account_deleted
          ? `Removed. ${name || "Member"} had no other clubs, so the account was deleted.`
          : `Removed from this club. ${resp.orders_soft_deleted || 0} order(s) moved to trash for 5 days.`,
      });
      // Give the user a moment to read the feedback before bouncing.
      setTimeout(() => onRemoved && onRemoved(), 900);
    } catch (err) {
      setFeedback({ kind: "error", text: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="member-remove-panel"
      className="mt-10 border-2 border-[#A33] bg-[#fff7f7] p-6 sm:p-7"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={20} className="text-[#8a1a1a] shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="overline" style={{ color: "#8a1a1a" }}>Danger Zone</div>
          <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Remove from this club</h2>
          <p className="text-sm text-[#4A4A4A] mt-2 leading-relaxed">
            Pulls <span className="font-semibold">{name || email}</span> off your club&rsquo;s roster. Their
            orders for this club are moved to the trash (restorable for 5 days),
            their wishlist for this club is wiped. If they don&rsquo;t belong to any
            other club, their account is fully deleted.
          </p>

          {!open ? (
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="member-remove-trigger"
              className="mt-4 px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold bg-[#8a1a1a] hover:bg-[#A33] text-[#F9F8F4] transition-colors"
            >
              Remove member
            </button>
          ) : (
            <div className="mt-5 bg-white border border-[#E5E3D8] p-5">
              <label className="block">
                <span className="label-elegant">Type the member&rsquo;s email to confirm</span>
                <input
                  type="text"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={email}
                  autoFocus
                  data-testid="member-remove-confirm-input"
                  className="mt-2 w-full px-3 py-2 border border-[#E5E3D8] bg-[#F9F8F4] font-mono text-sm focus:outline-none focus:border-[#8a1a1a]"
                />
              </label>
              <div className="mt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={reset}
                  disabled={busy}
                  data-testid="member-remove-cancel"
                  className="btn-secondary text-xs"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy || !matches}
                  data-testid="member-remove-submit"
                  className="px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold bg-[#8a1a1a] hover:bg-[#A33] text-[#F9F8F4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {busy ? "Removing…" : "Remove permanently"}
                </button>
              </div>
            </div>
          )}

          {feedback && (
            <div
              data-testid="member-remove-feedback"
              className={`mt-4 px-4 py-3 text-sm border ${
                feedback.kind === "ok"
                  ? "border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]"
                  : "border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]"
              }`}
            >
              {feedback.text}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function BackLink() {
  return (
    <Link
      to="/admin/members"
      data-testid="member-detail-back"
      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#1A422D] hover:underline"
    >
      <ChevronLeft size={14} /> Back to Members
    </Link>
  );
}

function Section({ title, subtitle, overline = "Crest", children, ...rest }) {
  return (
    <section className="surface-card p-6 sm:p-7" {...rest}>
      <div className="overline">{overline}</div>
      <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">{title}</h2>
      {subtitle && <p className="text-xs text-[#7A7A7A] mt-1">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Chips({ items, accent }) {
  if (!items || items.length === 0) return <Empty>Not set.</Empty>;
  const isBrass = accent === "brass";
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((b) => (
        <span
          key={b}
          className={`px-3 py-1 text-xs border ${
            isBrass
              ? "border-[#B59A5A] bg-[#FBF6E8] text-[#8a6f33]"
              : "border-[#E5E3D8] bg-white text-[#1A422D]"
          }`}
        >
          {b}
        </span>
      ))}
    </div>
  );
}

function Empty({ children }) {
  return <p className="text-sm text-[#9A9A9A]">{children}</p>;
}

/**
 * One row in the Club Affiliations list on the admin dossier. Renders the
 * club name + #member_number, plus an inline "Edit #" affordance that only
 * touches this admin's club (backend enforces tenant scope). Non-home clubs
 * that don't belong to this admin's tenant will 404 when edited — the row
 * still shows an Edit button but surfaces the API error inline.
 */
function ClubAffiliationRow({ club, userId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(club.member_number || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const start = () => {
    setValue(club.member_number || "");
    setErr("");
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setErr("");
  };
  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setErr("Member number cannot be empty.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.patch(
        `/admin/members/${userId}/club-number`,
        { member_number: trimmed },
      );
      onSaved?.(data.member);
      setEditing(false);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      data-testid={`admin-club-affiliation-${club.club_id || club.name}`}
      className="text-sm flex items-start justify-between gap-3 border-b border-[#E5E3D8] pb-2 last:border-0 last:pb-0"
    >
      <div className="min-w-0 flex-1">
        <div className="text-[#1A1A1A]">{club.name}</div>
        {editing ? (
          <div className="mt-1 flex items-center gap-2">
            <span className="text-xs text-[#7A7A7A] font-mono">#</span>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              data-testid="admin-club-number-input"
              className="input-field h-8 text-xs font-mono max-w-[140px] py-1"
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") cancel();
              }}
            />
            <button
              type="button"
              onClick={save}
              disabled={busy}
              data-testid="admin-club-number-save"
              className="text-[11px] uppercase tracking-wider text-[#1A422D] hover:underline disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={cancel}
              disabled={busy}
              data-testid="admin-club-number-cancel"
              className="text-[11px] uppercase tracking-wider text-[#7A7A7A] hover:underline disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="text-xs font-mono text-[#1A422D] mt-0.5">#{club.member_number}</div>
        )}
        {err && (
          <div className="text-[11px] text-[#8a1a1a] mt-1">{err}</div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {!editing && (
          <button
            type="button"
            onClick={start}
            data-testid="admin-club-number-edit"
            className="text-[11px] uppercase tracking-wider text-[#1A422D] hover:underline"
          >
            Edit #
          </button>
        )}
        {club.is_home && (
          <span className="text-[10px] uppercase tracking-wider px-2 py-1 border border-[#1A422D] text-[#1A422D] bg-[#E9F0EB]">
            Home
          </span>
        )}
      </div>
    </li>
  );
}
